import React, { useEffect } from 'react';
import { Dashboard as DashboardLayout } from 'layouts';
import { Box, SpeedDial, SpeedDialAction } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { getColumns } from './columns';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  blockUserAdmin,
  deleteUser,
  showAllUsers,
} from 'js/actions/userActions';
import Swal from 'sweetalert2';
import { Add, Settings } from '@mui/icons-material';

// ==============================
// Componentes
// ==============================
// Componente para la barra de búsqueda rápida en el toolbar de la DataGrid
function QuickSearchToolbar() {
  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'flex-end', // Alinea el filtro a la derecha
      }}
    >
      <GridToolbarQuickFilter /> {/* Barra de búsqueda rápida para la tabla */}
    </Box>
  );
}

// ==============================
// Componente principal
// ==============================
// Main component to display the user list
export default function UsersList() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { corpId } = useParams();
  const { users } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.user);
  const navigation = useNavigate();

  useEffect(() => {
    // Obtener todos los usuarios cuando cambia corpId
    dispatch(showAllUsers(corpId));
  }, [corpId]);

  // ==============================
  // Funciones para manejar acciones de usuario
  // ==============================
  // Función para eliminar un usuario
  const deleteUserData = (user) => {
    Swal.fire({
      title: intl.formatMessage({
        id: 'app.users.usersList.corpusers.msgdelete.title',
      }),
      text: intl.formatMessage({
        id: 'app.users.usersList.corpusers.msgdelete.text',
      }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({
        id: 'app.users.usersList.corpusers.msgdelete.confirm',
      }),
      cancelButtonText: intl.formatMessage({
        id: 'app.users.usersList.corpusers.msgdelete.cancel',
      }),
    }).then((result) => {
      if (result.value == true) {
        dispatch(deleteUser(user));
      }
    });
  };

  // Función para bloquear/desbloquear un usuario
  const updateUser = (user) => {
    const newActiveState = user.active === 1 ? 0 : 1;
    dispatch(blockUserAdmin(user.id, newActiveState));
    const message = user.active === 1 ? 'locked' : 'unlocked';
    Swal.fire({
      title: intl.formatMessage({ id: `app.users.usersList.${message}User` }),
      text: `${user.firstname} ${user.lastname} ${intl.formatMessage({ id: `app.users.usersList.${message}UserText` })}`,
      icon: 'success',
    });
  };

  // ==============================
  // Configuración de la DataGrid
  // ==============================
  const columns = getColumns(intl, updateUser, deleteUserData, user, corpId);

  const handleNavigation = () => {
    navigation(`/newUser/${corpId}`);
  };

  return (
    <DashboardLayout title="Usuarios">
      <Box p={3}>
        <Box mt={2} height={500}>
          <DataGrid
            columns={columns}
            rows={users}
            // Disable row selection
            rowSelection={false}
            initialState={{
              ...users.initialState, // Estado inicial de la tabla
              pagination: {
                ...users.initialState?.pagination, // Estado inicial de la paginación
                paginationModel: {
                  pageSize: 5, // Tamaño de la página
                },
              },
            }}
            slots={{ toolbar: QuickSearchToolbar }} // Usa el componente QuickSearchToolbar para la barra de búsqueda
            sx={{
              paddingTop: '1rem',
              paddingBottom: '1rem',
              borderRadius: '0.5rem',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'background.paper', // Color de fondo de la tabla
              border: '1px solid #ccc',
              borderColor: '#ccc',
              '& .MuiDataGrid-root': {
                border: 'none', // Eliminar borde de la tabla
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 1)', // Borde inferior de la celda
                maxHeight: '60px', // Altura máxima de la celda
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid rgb(199, 199, 199)', // Borde inferior del encabezado de columna
              },
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid rgb(199, 199, 199)', // Borde inferior de la fila
                maxHeight: '60px', // Altura máxima de la fila
              },
            }}
          />
        </Box>
        <SpeedDial
          ariaLabel="Configuraciones"
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            '& .MuiFab-primary': {
              width: 80, // Ajusta el tamaño del botón principal
              height: 80,
              backgroundColor: '#A53693', // Cambia el color de fondo
              '&:hover': {
                backgroundColor: '#6A1A5F',
              },
            },
          }}
          icon={
            <Settings
              sx={{
                width: 40,
                height: 40,
                transform: 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'rotate(180deg)',
                  backgroundColor: '#6A1A5F',
                },
              }}
            />
          }
        >
          <SpeedDialAction
            icon={<Add />}
            onClick={() => handleNavigation()}
            tooltipTitle="Nueva alerta"
            sx={{
              backgroundColor: '#00AFEF',
              color: '#fff',
              width: '60px',
              height: '60px',
              fontSize: '1.5em',
              '&:hover': {
                backgroundColor: 'rgb(0, 146, 199)',
              },
            }}
          />
        </SpeedDial>
      </Box>
    </DashboardLayout>
  );
}
